import React, { useState, useContext } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  ArrowLeft, 
  Save, 
  Smartphone, 
  Tag, 
  Palette,
  HardDrive,
  Cpu,
  FileText,
  Hash,
  Building2,
  Package
} from 'lucide-react';
import { AuthContext } from '../../context/AuthContext';
import { useTagsDisponibles } from '../../hooks/useTagsDisponibles';
import { createDispositivo } from '../../apis/dispositivosApi';

interface FormData {
  sentinel_id: string;
  nombre_dispositivo: string;
  imei: string;
  numero_serie: string;
  marca: string;
  modelo: string;
  version_android?: string;
  version_ios?: string;
  color: string;
  capacidad_almacenamiento: string;
  ram: string;
  observaciones?: string;
}

const AgregarDispositivo: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const { tagsDisponibles, isLoading: loadingTags } = useTagsDisponibles();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tipoDispositivo, setTipoDispositivo] = useState<'android' | 'ios'>('android');

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm<FormData>();

  const watchedSentinel = watch('sentinel_id');

  const onSubmit = async (data: FormData) => {
    if (!token) {
      toast.error('No hay token de autenticación');
      return;
    }

    setIsSubmitting(true);
    try {
      // Limpiar campos según el tipo de dispositivo
      const dispositivoData = {
        ...data,
        version_android: tipoDispositivo === 'android' ? data.version_android : undefined,
        version_ios: tipoDispositivo === 'ios' ? data.version_ios : undefined,
      };

      await createDispositivo(dispositivoData, token);
      
      toast.success('Dispositivo creado exitosamente');
      navigate('/dispositivos');
    } catch (error: any) {
      console.error('Error al crear dispositivo:', error);
      
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.data?.errors) {
        // Mostrar errores de validación
        const errors = error.response.data.errors;
        Object.keys(errors).forEach(key => {
          toast.error(`${key}: ${errors[key][0]}`);
        });
      } else {
        toast.error('Error al crear el dispositivo');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTipoDispositivoChange = (tipo: 'android' | 'ios') => {
    setTipoDispositivo(tipo);
    // Limpiar el campo de versión del tipo anterior
    if (tipo === 'android') {
      setValue('version_ios', '');
    } else {
      setValue('version_android', '');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dispositivos')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Volver a Dispositivos
          </button>
          
          <div className="flex items-center">
            <div className="bg-blue-100 p-3 rounded-lg mr-4">
              <Smartphone className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Agregar Dispositivo</h1>
              <p className="text-gray-600 mt-1">Complete la información del nuevo dispositivo Samsung</p>
            </div>
          </div>
        </div>

        {/* Formulario */}
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
            
            {/* Tag Selection */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <Tag className="w-5 h-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Selección de Sentinel (OnAsset)</h3>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sentinel Disponible *
                </label>
                <select
                  {...register('sentinel_id', { required: 'El sentinel es requerido' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={loadingTags}
                >
                  <option value="">Seleccione un sentinel disponible</option>
                  {tagsDisponibles.map((sentinel) => (
                    <option key={sentinel.sentinelId} value={sentinel.sentinelId}>
                      {sentinel.displayName} - {sentinel.sentinelType}
                    </option>
                  ))}
                </select>
                {errors.sentinel_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.sentinel_id.message}</p>
                )}
                {loadingTags && (
                  <p className="mt-1 text-sm text-gray-500">Cargando sentinels disponibles...</p>
                )}
                {watchedSentinel && (
                  <div className="mt-2 p-2 bg-blue-100 rounded text-sm">
                    <p className="text-blue-800">
                      <strong>Sentinel seleccionado:</strong> {watchedSentinel}
                    </p>
                    {tagsDisponibles.find(s => s.sentinelId === watchedSentinel) && (
                      <p className="text-blue-600 text-xs mt-1">
                        Tipo: {tagsDisponibles.find(s => s.sentinelId === watchedSentinel)?.sentinelType}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Información Básica */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <Smartphone className="w-5 h-5 text-gray-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Información Básica</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre del Dispositivo *
                  </label>
                  <input
                    type="text"
                    {...register('nombre_dispositivo', { required: 'El nombre del dispositivo es requerido' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ej: Samsung Galaxy S24"
                  />
                  {errors.nombre_dispositivo && (
                    <p className="mt-1 text-sm text-red-600">{errors.nombre_dispositivo.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    IMEI *
                  </label>
                  <input
                    type="text"
                    {...register('imei', { 
                      required: 'El IMEI es requerido',
                      pattern: {
                        value: /^[0-9]{15}$/,
                        message: 'El IMEI debe tener 15 dígitos'
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="123456789012345"
                    maxLength={15}
                  />
                  {errors.imei && (
                    <p className="mt-1 text-sm text-red-600">{errors.imei.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Número de Serie *
                  </label>
                  <input
                    type="text"
                    {...register('numero_serie', { required: 'El número de serie es requerido' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ej: R58N123ABCD"
                  />
                  {errors.numero_serie && (
                    <p className="mt-1 text-sm text-red-600">{errors.numero_serie.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Marca *
                  </label>
                  <input
                    type="text"
                    {...register('marca', { required: 'La marca es requerida' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ej: Samsung"
                  />
                  {errors.marca && (
                    <p className="mt-1 text-sm text-red-600">{errors.marca.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Modelo *
                  </label>
                  <input
                    type="text"
                    {...register('modelo', { required: 'El modelo es requerido' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ej: Galaxy S24 Ultra"
                  />
                  {errors.modelo && (
                    <p className="mt-1 text-sm text-red-600">{errors.modelo.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Sistema Operativo */}
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <Package className="w-5 h-5 text-green-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Sistema Operativo</h3>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Dispositivo *
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="android"
                      checked={tipoDispositivo === 'android'}
                      onChange={(e) => handleTipoDispositivoChange(e.target.value as 'android' | 'ios')}
                      className="mr-2"
                    />
                    Android
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="ios"
                      checked={tipoDispositivo === 'ios'}
                      onChange={(e) => handleTipoDispositivoChange(e.target.value as 'android' | 'ios')}
                      className="mr-2"
                    />
                    iOS (iPhone)
                  </label>
                </div>
              </div>

              {tipoDispositivo === 'android' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Versión de Android
                  </label>
                  <input
                    type="text"
                    {...register('version_android')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ej: 14.0"
                  />
                </div>
              )}

              {tipoDispositivo === 'ios' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Versión de iOS
                  </label>
                  <input
                    type="text"
                    {...register('version_ios')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ej: 17.5"
                  />
                </div>
              )}
            </div>

            {/* Características Físicas */}
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <Palette className="w-5 h-5 text-purple-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Características Físicas</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Color *
                  </label>
                  <input
                    type="text"
                    {...register('color', { required: 'El color es requerido' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ej: Negro Titanio"
                  />
                  {errors.color && (
                    <p className="mt-1 text-sm text-red-600">{errors.color.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Capacidad de Almacenamiento *
                  </label>
                  <select
                    {...register('capacidad_almacenamiento', { required: 'La capacidad es requerida' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Seleccionar</option>
                    <option value="64GB">64GB</option>
                    <option value="128GB">128GB</option>
                    <option value="256GB">256GB</option>
                    <option value="512GB">512GB</option>
                    <option value="1TB">1TB</option>
                  </select>
                  {errors.capacidad_almacenamiento && (
                    <p className="mt-1 text-sm text-red-600">{errors.capacidad_almacenamiento.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    RAM *
                  </label>
                  <select
                    {...register('ram', { required: 'La RAM es requerida' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Seleccionar</option>
                    <option value="4GB">4GB</option>
                    <option value="6GB">6GB</option>
                    <option value="8GB">8GB</option>
                    <option value="12GB">12GB</option>
                    <option value="16GB">16GB</option>
                  </select>
                  {errors.ram && (
                    <p className="mt-1 text-sm text-red-600">{errors.ram.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Observaciones */}
            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <FileText className="w-5 h-5 text-yellow-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Observaciones</h3>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Observaciones (Opcional)
                </label>
                <textarea
                  {...register('observaciones')}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Información adicional sobre el dispositivo..."
                />
              </div>
            </div>

            {/* Botones */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/dispositivos')}
                className="px-6 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar Dispositivo
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AgregarDispositivo;
